import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiDollarSign, 
  FiShoppingBag,
  FiPlus,
  FiCopy,
  FiCheck
} from 'react-icons/fi';
import api from '../../lib/api';
import AffiliateNavbar from '../../components/AffiliateNavbar';
import styles from './AffiliateDashboard.module.css';

interface DashboardStats {
  totalEarned: number;
  availableBalance: number;
  totalSales: number;
  activePromoCodes: number;
  cryptoAddress: string | null;
  cryptoNetwork: string | null;
}

interface PromoCode {
  id: string;
  code: string;
  discountPercent: number;
  active: boolean;
  usageCount: number;
  maxUsage: number | null;
  totalSales: number;
  totalEarned: number;
  createdAt: string;
}

interface Commission {
  id: string;
  orderAmount: number;
  commissionAmount: number;
  createdAt: string;
  order: { orderNumber: string };
  promoCode: { code: string };
}

interface WithdrawRequest {
  id: string;
  amount: number;
  cryptoAddress: string;
  cryptoNetwork: string | null;
  status: string;
  adminNote: string | null;
  createdAt: string;
}

const AffiliateDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [withdraws, setWithdraws] = useState<WithdrawRequest[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawNetwork, setWithdrawNetwork] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState<'promoCodes' | 'commissions' | 'withdraws'>('promoCodes');

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('affiliateToken');
      if (!token) {
        navigate('/affiliate/login');
        return;
      }

      // The api interceptor will automatically attach the token
      const [dashboardRes, promoCodesRes, commissionsRes, withdrawsRes] = await Promise.all([
        api.get('/affiliates/dashboard'),
        api.get('/affiliates/promo-codes'),
        api.get('/affiliates/commissions'),
        api.get('/affiliates/withdraws'),
      ]);

      setStats(dashboardRes.data.stats);
      setPromoCodes(promoCodesRes.data.promoCodes);
      setCommissions(commissionsRes.data.commissions);
      setWithdraws(withdrawsRes.data.withdrawRequests);
      
      if (dashboardRes.data.stats.cryptoAddress) {
        setWithdrawAddress(dashboardRes.data.stats.cryptoAddress);
      }
      if (dashboardRes.data.stats.cryptoNetwork) {
        setWithdrawNetwork(dashboardRes.data.stats.cryptoNetwork);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        localStorage.removeItem('affiliateToken');
        navigate('/affiliate/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      await api.post('/affiliates/promo-codes', { code: newPromoCode });
      
      setNewPromoCode('');
      setShowCreatePromo(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create promo code');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleTogglePromoCode = async (id: string, active: boolean) => {
    try {
      await api.put(`/affiliates/promo-codes/${id}`, { active });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update promo code');
    }
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!withdrawAddress.trim()) {
      alert('Please provide a crypto address');
      return;
    }

    if (!withdrawNetwork.trim()) {
      alert('Please select a network');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (amount < 5) {
      alert('Minimum withdrawal amount is $5');
      return;
    }

    if (stats && amount > stats.availableBalance) {
      alert('Insufficient balance');
      return;
    }

    setWithdrawLoading(true);

    try {
      await api.post('/affiliates/withdraw', { 
        amount, 
        cryptoAddress: withdrawAddress,
        cryptoNetwork: withdrawNetwork
      });
      
      setWithdrawAmount('');
      setShowWithdraw(false);
      fetchData();
      alert('Withdrawal request created successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create withdrawal request');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!withdrawNetwork.trim()) {
      alert('Please select a network');
      return;
    }
    try {
      await api.put('/affiliates/profile', { 
        cryptoAddress: withdrawAddress,
        cryptoNetwork: withdrawNetwork
      });
      alert('Crypto address updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update profile');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <>
      <AffiliateNavbar />
      <div className={styles.dashboard}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Affiliate Dashboard</h1>
          </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiDollarSign />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>${stats?.totalEarned.toFixed(2)}</div>
              <div className={styles.statLabel}>Total Earned</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiDollarSign />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>${stats?.availableBalance.toFixed(2)}</div>
              <div className={styles.statLabel}>Available Balance</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiShoppingBag />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats?.totalSales}</div>
              <div className={styles.statLabel}>Total Sales</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <FiShoppingBag />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats?.activePromoCodes}</div>
              <div className={styles.statLabel}>Active Codes</div>
            </div>
          </div>
        </div>

        {/* Crypto Address */}
        <div className={styles.section}>
          <h3>Crypto Address (USDT/USDC)</h3>
          <div className={styles.formGroup}>
            <label>Network</label>
            <select
              value={withdrawNetwork}
              onChange={(e) => setWithdrawNetwork(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                fontSize: '0.9375rem',
                color: 'var(--text-primary)',
                background: 'var(--bg-elevated)',
                fontFamily: 'var(--font-sans)',
                marginBottom: '1rem',
              }}
            >
              <option value="">Select network</option>
              <option value="BEP20">BEP20 (Binance Smart Chain)</option>
              <option value="ERC20">ERC20 (Ethereum)</option>
              <option value="TRC20">TRC20 (Tron)</option>
              <option value="SOL">Solana</option>
            </select>
          </div>
          <div className={styles.addressInput}>
            <input
              type="text"
              placeholder="Enter your crypto address for withdrawals"
              value={withdrawAddress}
              onChange={(e) => setWithdrawAddress(e.target.value)}
            />
            <button onClick={handleUpdateProfile}>Update</button>
          </div>
        </div>

        {/* Withdraw Button */}
        <div className={styles.section}>
          <button 
            className={styles.withdrawBtn}
            onClick={() => setShowWithdraw(true)}
            disabled={!stats || stats.availableBalance < 5}
          >
            <FiDollarSign /> Request Withdrawal
          </button>
          {stats && stats.availableBalance < 5 && (
            <small style={{ color: 'var(--text-muted)', marginTop: '0.5rem', display: 'block' }}>
              Minimum balance for withdrawal: $5.00
            </small>
          )}
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
        <button 
          className={activeTab === 'promoCodes' ? styles.tabActive : ''}
          onClick={() => setActiveTab('promoCodes')}
        >
          Promo Codes
        </button>
        <button 
          className={activeTab === 'commissions' ? styles.tabActive : ''}
          onClick={() => setActiveTab('commissions')}
        >
          Commissions
        </button>
        <button 
          className={activeTab === 'withdraws' ? styles.tabActive : ''}
          onClick={() => setActiveTab('withdraws')}
        >
          Withdrawals
        </button>
      </div>

        {/* Promo Codes Tab */}
        {activeTab === 'promoCodes' && (
          <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Your Promo Codes</h3>
            <button className={styles.addBtn} onClick={() => setShowCreatePromo(true)}>
              <FiPlus /> Create Code
            </button>
          </div>

          <div className={styles.table}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Usage</th>
                  <th>Sales</th>
                  <th>Earned</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((code) => (
                  <tr key={code.id}>
                    <td>
                      <strong>{code.code}</strong>
                      <button 
                        className={styles.copyIcon}
                        onClick={() => copyToClipboard(code.code)}
                        title="Copy code"
                      >
                        <FiCopy size={14} />
                      </button>
                    </td>
                    <td>{code.discountPercent}%</td>
                    <td>{code.usageCount} / {code.maxUsage !== null ? code.maxUsage : '∞'}</td>
                    <td>{code.totalSales}</td>
                    <td>${code.totalEarned.toFixed(2)}</td>
                    <td>
                      <span className={code.active ? styles.statusActive : styles.statusInactive}>
                        {code.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.toggleBtn}
                        onClick={() => handleTogglePromoCode(code.id, !code.active)}
                      >
                        {code.active ? <FiCheck /> : <FiCheck />}
                        {code.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {promoCodes.length === 0 && (
              <div className={styles.emptyState}>No promo codes yet. Create your first one!</div>
            )}
          </div>
        </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className={styles.section}>
          <h3>Commission History</h3>
          <div className={styles.table}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order</th>
                  <th>Promo Code</th>
                  <th>Order Amount</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((comm) => (
                  <tr key={comm.id}>
                    <td>{new Date(comm.createdAt).toLocaleDateString()}</td>
                    <td>{comm.order.orderNumber}</td>
                    <td>{comm.promoCode.code}</td>
                    <td>${comm.orderAmount.toFixed(2)}</td>
                    <td className={styles.commission}>${comm.commissionAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {commissions.length === 0 && (
              <div className={styles.emptyState}>No commissions yet</div>
            )}
          </div>
        </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdraws' && (
          <div className={styles.section}>
          <h3>Withdrawal History</h3>
          <div className={styles.table}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Network</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {withdraws.map((withdraw) => (
                  <tr key={withdraw.id}>
                    <td>{new Date(withdraw.createdAt).toLocaleDateString()}</td>
                    <td>${withdraw.amount.toFixed(2)}</td>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem' }}>{withdraw.cryptoNetwork || '-'}</span></td>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{withdraw.cryptoAddress.substring(0, 15)}...</span></td>
                    <td>
                      <span className={styles[`status${withdraw.status}`]}>
                        {withdraw.status}
                      </span>
                    </td>
                    <td>{withdraw.adminNote || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {withdraws.length === 0 && (
              <div className={styles.emptyState}>No withdrawal requests yet</div>
            )}
          </div>
        </div>
        )}

        {/* Create Promo Code Modal */}
        {showCreatePromo && (
        <div className={styles.modal} onClick={() => setShowCreatePromo(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Create Promo Code</h3>
            <form onSubmit={handleCreatePromoCode}>
              <div className={styles.formGroup}>
                <label>Code (6-20 alphanumeric characters)</label>
                <input
                  type="text"
                  value={newPromoCode}
                  onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                  placeholder="MYCODE"
                  required
                  minLength={6}
                  maxLength={20}
                  pattern="[A-Z0-9]+"
                />
                <small>Discount: 5% (fixed for affiliates)</small>
              </div>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setShowCreatePromo(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={createLoading}>
                  {createLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Withdraw Modal */}
        {showWithdraw && (
        <div className={styles.modal} onClick={() => setShowWithdraw(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Request Withdrawal</h3>
            <form onSubmit={handleWithdraw}>
              <div className={styles.formGroup}>
                <label>Amount (min: $5.00, max: ${stats?.availableBalance.toFixed(2)})</label>
                <input
                  type="number"
                  step="0.01"
                  min="5"
                  max={stats?.availableBalance}
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="5.00"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Network</label>
                <select
                  value={withdrawNetwork}
                  onChange={(e) => setWithdrawNetwork(e.target.value)}
                  required
                >
                  <option value="">Select network</option>
                  <option value="BEP20">BEP20 (Binance Smart Chain)</option>
                  <option value="ERC20">ERC20 (Ethereum)</option>
                  <option value="TRC20">TRC20 (Tron)</option>
                  <option value="SOL">Solana</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Crypto Address (USDT/USDC)</label>
                <input
                  type="text"
                  value={withdrawAddress}
                  onChange={(e) => setWithdrawAddress(e.target.value)}
                  placeholder="0x..."
                  required
                />
              </div>
              <div className={styles.warning}>
                Withdrawals are processed manually within 24-48 hours.
              </div>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setShowWithdraw(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={withdrawLoading}>
                  {withdrawLoading ? 'Submitting...' : 'Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
        </div>
      </div>
    </>
  );
};

export default AffiliateDashboard;
